import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTaskContext } from "../../providers/TaskProvider";
import {
  deleteTaskAttachment,
  fetchTaskAttachments,
} from "../../services/supabase";

export default function TaskAttachments({
  taskId,
  isEditing = false,
  onAttachmentDelete = null,
  onAttachmentsLoaded = null,
}) {
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const { refreshTasks } = useTaskContext();

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth <= 768 ||
          "ontouchstart" in window ||
          navigator.maxTouchPoints > 0
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const loadAttachments = async () => {
      if (!taskId) return;

      setIsLoading(true);
      try {
        const data = await fetchTaskAttachments(taskId);
        setAttachments(data || []);

        // Notify parent component about attachment count
        if (onAttachmentsLoaded) {
          onAttachmentsLoaded(data ? data.length : 0);
        }

        setError(null);
      } catch (err) {
        // Silently handle the error without showing it to the user
        console.error("Error loading attachments:", err);
        setError(err);
        setAttachments([]);

        // Notify parent that we have no attachments
        if (onAttachmentsLoaded) {
          onAttachmentsLoaded(0);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadAttachments();
  }, [taskId, onAttachmentsLoaded]);

  const handleDelete = async (attachmentId) => {
    if (window.confirm("Are you sure you want to delete this attachment?")) {
      const success = await deleteTaskAttachment(attachmentId);
      if (success) {
        const updatedAttachments = attachments.filter(
          (a) => a.id !== attachmentId
        );
        setAttachments(updatedAttachments);

        // Notify parent about updated attachment count
        if (onAttachmentsLoaded) {
          onAttachmentsLoaded(updatedAttachments.length);
        }

        if (onAttachmentDelete) {
          onAttachmentDelete(attachmentId);
        }

        // Refresh the task list to ensure all components are up to date
        refreshTasks();
      }
    }
  };

  const formatFileName = (fileName) => {
    if (!fileName) return "";
    if (fileName.length <= 15) return fileName;
    const ext = fileName.split(".").pop();
    const name = fileName.substring(0, fileName.length - ext.length - 1);
    return `${name.substring(0, 10)}...${ext}`;
  };

  // Check if file is a Markdown file
  const isMarkdownFile = (fileName) => {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith(".md");
  };

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    if (!fileName) return "file-earmark";

    const ext = fileName.split(".").pop().toLowerCase();
    switch (ext) {
      case "pdf":
        return "file-earmark-pdf";
      case "md":
        return "file-earmark-text";
      case "doc":
      case "docx":
        return "file-earmark-word";
      case "xls":
      case "xlsx":
        return "file-earmark-spreadsheet";
      case "ppt":
      case "pptx":
        return "file-earmark-slides";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "file-earmark-image";
      default:
        return "file-earmark";
    }
  };

  // Icon size based on device
  const iconSize = isMobile ? "1.2rem" : "1rem";

  // If there are no attachments, return null
  if (attachments.length === 0 && !isLoading) {
    return null;
  }

  if (isLoading) {
    return <div className="text-xs text-gray-400">Loading attachments...</div>;
  }

  return (
    <div className="mt-2">
      <div className="text-xs text-gray-400 mb-1">Attachments:</div>
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center justify-between text-xs"
          >
            {isMarkdownFile(attachment.file_name) ? (
              <Link
                to={`/view-markdown/${encodeURIComponent(attachment.file_url)}`}
                className="text-[#4db1e0] hover:text-[#3da0cf] flex items-center"
              >
                <sl-icon
                  name={getFileIcon(attachment.file_name)}
                  class="mr-1"
                  style={{ fontSize: iconSize }}
                ></sl-icon>
                {formatFileName(attachment.file_name)}
              </Link>
            ) : (
              <a
                href={attachment.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4db1e0] hover:text-[#3da0cf] flex items-center"
              >
                <sl-icon
                  name={getFileIcon(attachment.file_name)}
                  class="mr-1"
                  style={{ fontSize: iconSize }}
                ></sl-icon>
                {formatFileName(attachment.file_name)}
              </a>
            )}

            {isEditing && (
              <button
                onClick={() => handleDelete(attachment.id)}
                className="text-gray-400 hover:text-red-400 ml-2 p-1"
                aria-label="Delete attachment"
              >
                <sl-icon name="trash" style={{ fontSize: iconSize }}></sl-icon>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
